import { ChevronDownIcon } from '@chakra-ui/icons';
import { Button, Center, Flex, Icon, Menu, MenuButton, MenuItem, MenuList, Spinner, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { PaginationState, getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { emailRepository } from 'api/EmailRepository';
import Card from 'components/card/Card';
import { useEffect, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import EmailTable from 'views/admin/Email Template/Component/EmailTable';
export default function EmailList() {
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);

  const fetchEmails = async () => {
    try {
      setLoading(true);

      const data = await emailRepository.getPaginatedEmails(
        pageIndex + 1,
        pageSize
      );

      setTableData(data);

      if (data.length < pageSize) {
        setTotalCount(pageIndex * pageSize + data.length);
      } else {
        setTotalCount((pageIndex + 1) * pageSize + 1);
      }
    } catch (err) {
      setError("Failed to load email templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [pageIndex, pageSize]);

  const table = useReactTable({
    data: tableData,
    columns: [],
    state: {
      pagination: { pageIndex, pageSize },
    },

    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize) || 1,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  const createPages = (count: number) => {
    let arr = [];
    for (let i = 1; i <= count; i++) arr.push(i);
    return arr;
  };

  const textColor = useColorModeValue("gray.700", "white");
  const brandColor = useColorModeValue("brand.500", "brand.400");

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Text color="red.500">{error}</Text>
      </Center>
    );
  }

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }}>
      <Card px="0px">
        <EmailTable tableData={tableData}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      </Card>
      {!globalFilter && (
        <Flex w="100%" justify="space-between" px="20px" pt="20px" pb="20px">
          <Menu>
            <Text m={2}>showing row per page</Text>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              colorScheme="gray"
              borderRadius="16px"
              px={5}
              h="44px"
            >
              {pageSize}
            </MenuButton>

            <MenuList zIndex={1000} boxShadow="lg">
              {[5, 10, 20].map((size) => (
                <MenuItem
                  key={size}
                  onClick={() =>
                    setPagination({
                      pageIndex: 0,
                      pageSize: size,
                    })
                  }
                >
                  {size}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
            <Button
              variant="no-effects"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              w="40px"
              h="40px"
              borderRadius="50%"
              border="1px solid lightgray"
            >
              <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
            </Button>

            {createPages(table.getPageCount()).map((pageNumber, index) => (
              <Button
                key={index}
                onClick={() => table.setPageIndex(pageNumber - 1)}
                w="40px"
                h="40px"
                borderRadius="50%"
                bg={pageIndex + 1 === pageNumber ? brandColor : "transparent"}
                border={pageIndex + 1 === pageNumber ? "none" : "1px solid lightgray"}
              >
                <Text color={pageIndex + 1 === pageNumber ? "#fff" : textColor}>
                  {pageNumber}
                </Text>
              </Button>
            ))}

            <Button
              variant="no-effects"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              w="40px"
              h="40px"
              borderRadius="50%"
              border="1px solid lightgray"
            >
              <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
            </Button>
          </Stack>

        </Flex>
      )}
    </Flex>
  );
}
